import { BellLight, BookOpenLight, ChatAltLight, ChatPlusSquareLight, VerticalCustom } from '@repo/go-icons';
import { TableSelect, TableNumberInput, TableCheckbox, TableCell } from '@repo/go-table';
import type { GridColDef } from '@repo/go-table';
import { Checkbox, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import type { i18n as i18nType } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { LogCurve, WellLog } from '../../shared/api/IWellDirectoryService';
import type { BaseParameterTypes } from '../../shared/baseParams';
import {
  baseParamsToMnemonic,
  depthSortedParameters,
  nameDictionary,
  timeSortedParametersNoAutoRecognized,
} from '../../shared/baseParams';
import { IconButton } from '../../shared/components/IconButton/IconButton';
import { Tag, type TStatus } from '../../shared/components/Tag/Tag';
import type { TDictObject } from '../../shared/IDirectoriesService';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import { convertDepthUnitValue } from '../../shared/utils';
import tableStyles from '../AlertsManagerTable.module.css';
import { ContextDetails } from '../components/ContextDetails/ContextDetails';
import { TableHeader } from '../components/TableHeader/TableHeader';
import { UserInfo } from '../components/UserInfo';
import { DRILLING_LOADS_MNEMONICS } from '../constants';
import type { TAlertRule, TSourceType, TOption } from '../types';
import { toOptions, uniqueOptions } from '../utils';

import styles from './AlertRulesTable.module.css';
import { type AlertRulesTableStore } from './AlertRulesTableStore';
import { AlertCell } from './components/AlertCell';
import { FIELD_TO_COLUMN_TITLE_KEY } from './FIELD_TO_COLUMN_TITLE_KEY';
import { AlertRule } from './services/AlertRule.entity';
import type { AlertRulesService } from './services/AlertRulesService';
import { ALERT_RULE_GROUPED_COLUMN_FIELDS } from './useColumnGroups';

type TTableColumn = GridColDef;

const ContextIcon = observer(({ entity, service }: { entity: AlertRule; service: AlertRulesService }) => {
  return AlertRule.hasContext(entity) ? (
    <Tooltip title={<ContextDetails service={service} entity={entity} />}>
      <ChatAltLight style={{ fontSize: 20 }} />
    </Tooltip>
  ) : (
    <ChatPlusSquareLight style={{ fontSize: 20 }} />
  );
});

const NotificationsHeader = observer(function NotificationsHeader({
  service,
  disabled,
}: {
  service: AlertRulesService;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const icon = <BellLight style={{ fontSize: 16 }} />;

  if (service.uiState.editing) {
    return (
      <Tooltip title={t('alertsManagerWidget:alertRules.columnTitle.showPopup')} delay={400}>
        {icon}
      </Tooltip>
    );
  }

  const rules = service.rows;
  const enabledCount = rules.filter((rule) => rule.notificationsEnabled).length;
  const allEnabled = rules.length > 0 && enabledCount === rules.length;
  const intermediate = enabledCount > 0 && !allEnabled;

  const selectionClassName: string | undefined = (() => {
    if (allEnabled) {
      return styles.notificationsHeaderBell_all;
    }

    if (intermediate) {
      return styles.notificationsHeaderBell_some;
    }

    return styles.notificationsHeaderBell_none;
  })();

  return (
    <Tooltip title={t('alertsManagerWidget:alertRules.columnTitle.showPopup')} delay={400}>
      <button
        type="button"
        className={clsx(styles.notificationsHeaderBell, selectionClassName)}
        aria-pressed={allEnabled}
        disabled={disabled || rules.length === 0}
        onClick={() => service.setAllAlertRulesNotificationsOption(!allEnabled)}
      >
        {icon}
      </button>
    </Tooltip>
  );
});

const JournalHeaderCheckbox = observer(function JournalHeaderCheckbox({
  service,
  disabled,
}: {
  service: AlertRulesService;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  if (!service.uiState.editing) {
    return (
      <Tooltip title={t('alertsManagerWidget:alertRules.columnTitle.saveToLog')} delay={400}>
        <BookOpenLight style={{ fontSize: 16 }} />
      </Tooltip>
    );
  }
  const rules = service.rows;
  const enabledCount = rules.filter((rule) => !!rule.data.journalEnabled).length;
  const allEnabled = rules.length > 0 && enabledCount === rules.length;
  const intermediate = enabledCount > 0 && !allEnabled;

  return (
    <Tooltip title={t('alertsManagerWidget:alertRules.columnTitle.saveToLog')} delay={400}>
      <span>
        <Checkbox
          size="s"
          checked={allEnabled}
          intermediate={intermediate}
          disabled={disabled || rules.length === 0}
          onChange={(value) => service.setAllJournalEnabled(value)}
        />
      </span>
    </Tooltip>
  );
});

const formatBoolean = (value: boolean, i18n: i18nType): string => {
  if (value) {
    return i18n.t('common:yes');
  }

  return i18n.t('common:no');
};

export const useTableColumns = (
  store: AlertRulesTableStore,
  alertRulesService: AlertRulesService,
  timeZonesStore: TimeZonesStore,
  isAltNames: boolean,
  defaultDepthUnit: 'm' | 'ft',
) => {
  const { t, i18n } = useTranslation();
  const editing = alertRulesService.uiState.editing;
  const scaleDepthUnit = alertRulesService.settingsStore.scaleDepthUnit;

  const sourceTypeOptions: TOption[] = useMemo(
    (): { label: string; value: TSourceType }[] =>
      [
        {
          label: t('alertsManagerWidget:alertRules.sourceTypeOptions.BASE_PARAMS'),
          value: 'GENERAL_PARAMETERS' as const,
        },
        alertRulesService.index === 'depth'
          ? {
              label: t('alertsManagerWidget:alertRules.sourceTypeOptions.DRILLING_LOADS'),
              value: 'DRILLING_LOADS' as const,
            }
          : null,
        alertRulesService.index === 'time'
          ? {
              label: t('alertsManagerWidget:alertRules.sourceTypeOptions.AUTOSUPERVISOR_OPERATIONS'),
              value: 'AUTOSUPERVISOR_OPERATIONS' as const,
            }
          : null,
        alertRulesService.index === 'time'
          ? {
              label: t('alertsManagerWidget:alertRules.sourceTypeOptions.AUTOSUPERVISOR_KPI_STATES'),
              value: 'AUTOSUPERVISOR_KPI_STATES' as const,
            }
          : null,
      ].filter((i): i is NonNullable<typeof i> => i != null),
    [t, alertRulesService.index],
  );

  const riskLevelOptions = useMemo(
    () => [
      { label: t('alertsManagerWidget:riskLevelOptions.NO_RISK'), value: 'NO_RISK', status: 'gray' },
      { label: t('alertsManagerWidget:riskLevelOptions.LOW_RISK'), value: 'LOW_RISK', status: 'blue' },
      { label: t('alertsManagerWidget:riskLevelOptions.MED_RISK'), value: 'MED_RISK', status: 'orange' },
      { label: t('alertsManagerWidget:riskLevelOptions.HIGH_RISK'), value: 'HIGH_RISK', status: 'red' },
    ],
    [t],
  );

  const anomalyTypeOptions: TOption[] = useMemo(
    () =>
      alertRulesService.anomalyTypes.map((anomalyType) => ({
        label: anomalyType.data['name']?.toString() ?? '',
        value: anomalyType.id as number,
      })),
    [alertRulesService.anomalyTypes],
  );

  const getAnomalySubtypeOptions = useCallback(
    function getAnomalySubtypeOptions(anomalyTypeId: number): TOption[] {
      return alertRulesService.anomalySubtypes
        .filter((anomalySubtype) => anomalySubtype.data['anomalyType'] === anomalyTypeId)
        .map((anomalySubtype) => ({
          label: anomalySubtype.data['name']?.toString() ?? '',
          value: anomalySubtype.id as number,
        }));
    },
    [alertRulesService.anomalySubtypes],
  );

  const unitOptions = useMemo(
    () =>
      alertRulesService.unitsList.map((unit) => ({
        label: unit.data['abbreviation']?.toString() ?? '',
        value: unit.id as number,
      })),
    [alertRulesService.unitsList],
  );

  const sectionTypeOptions: TOption[] = useMemo(
    () => toOptions(alertRulesService.sectionTypes),
    [alertRulesService.sectionTypes],
  );

  const parameterTypes = alertRulesService.parameterTypes;
  const parameterTypeByMnemonic = useMemo(() => {
    return parameterTypes.reduce((acc, parameterType) => {
      const displayMnemonic = parameterType.data['displayMnemonic'];
      acc.set(displayMnemonic as string, parameterType);

      return acc;
    }, new Map<string, (typeof parameterTypes)[number]>());
  }, [parameterTypes]);

  const drillingLoadsOptions = useMemo(() => {
    return DRILLING_LOADS_MNEMONICS.map((mnemonic) => {
      const parameterType = parameterTypeByMnemonic.get(mnemonic);
      const generalName = parameterType?.data['generalName'];
      const name: string = isAltNames && !!generalName ? (generalName as string) : mnemonic;

      return {
        label: name,
        value: mnemonic,
      };
    });
  }, [isAltNames, parameterTypeByMnemonic]);

  const drillingLoadsReferenceOptions = useMemo(
    () =>
      alertRulesService.parameterTypes
        .filter((parameterType) => parameterType.data['displayMnemonic']?.toString().startsWith('PL_') ?? false)
        .map((parameterType) => {
          const name =
            isAltNames && parameterType.data['generalName']
              ? (parameterType.data['generalName'] as string)
              : (parameterType.data['displayMnemonic']?.toString() ?? '');
          return {
            label: name ?? '',
            value: parameterType.id as number,
            optionLabel: name ?? '',
          };
        }),
    [alertRulesService.parameterTypes, isAltNames],
  );

  const getSourceTypeOptions = useCallback(
    function getSourceTypeOptions(source: TSourceType): TOption[] {
      if (source === 'GENERAL_PARAMETERS') {
        const parameters =
          alertRulesService.index === 'time' ? timeSortedParametersNoAutoRecognized : depthSortedParameters;
        return parameters.map((parameter) => ({
          label: t(`alertsManagerWidget:baseParameters.${nameDictionary[parameter]}`),
          value: parameter,
        }));
      }
      if (source === 'AUTOSUPERVISOR_OPERATIONS') {
        return alertRulesService.autosupervisorOperations.map((operation) => ({
          label: operation.data['name']?.toString() ?? '',
          value: operation.data.code as number,
        }));
      }
      if (source === 'AUTOSUPERVISOR_KPI_STATES') {
        return alertRulesService.autosupervisorKPIstates.map((kpi) => ({
          label: kpi.data['name']?.toString() ?? '',
          value: kpi.data.code as number,
        }));
      }
      if (source === 'DRILLING_LOADS') {
        return drillingLoadsOptions;
      }
      return [];
    },
    [
      alertRulesService.autosupervisorKPIstates,
      alertRulesService.autosupervisorOperations,
      alertRulesService.index,
      drillingLoadsOptions,
      t,
    ],
  );

  const columns = useMemo<TTableColumn[]>((): GridColDef<AlertRule>[] => {
    const getGeneralParameterCurve = (row: AlertRule) => {
      if (row.data.sourceType === 'GENERAL_PARAMETERS') {
        const sourceOption = row.data.sourceOption;
        if (typeof sourceOption !== 'string') {
          return null;
        }

        const mnemonics = baseParamsToMnemonic[sourceOption as BaseParameterTypes] ?? [];
        if (!mnemonics.length) {
          return null;
        }

        for (const mnemonic of mnemonics) {
          const parameterType = parameterTypeByMnemonic.get(mnemonic);
          if (parameterType) {
            return parameterType;
          }
        }
        return null;
      }
      return null;
    };
    const unitOptionsFiltered = (row: AlertRule) => {
      if (row.data.sourceType === 'AUTOSUPERVISOR_OPERATIONS' || row.data.sourceType === 'AUTOSUPERVISOR_KPI_STATES') {
        return unitOptions.filter((option) => option.label === 's' || option.label === 'min');
      }
      const getUnitOptionsByParameterType = (parameterType: TDictObject) => {
        const classOfUnitId = parameterType.data['classOfUnitId'];
        const classOfUnit = alertRulesService.classOfUnits.find((classOfUnit) => classOfUnit.id === classOfUnitId);
        const unitIds: number[] = classOfUnit?.data['unitIds'] as number[];
        if (unitIds) {
          return unitOptions.filter((option) => unitIds.includes(option.value));
        }
        return unitOptions;
      };
      if (row.data.sourceType === 'GENERAL_PARAMETERS') {
        const parameterType = getGeneralParameterCurve(row);
        return parameterType ? getUnitOptionsByParameterType(parameterType) : unitOptions;
      }

      if (row.data.sourceType === 'DRILLING_LOADS') {
        const parameterType = parameterTypeByMnemonic.get(row.data.sourceOption as string);
        return parameterType ? getUnitOptionsByParameterType(parameterType) : unitOptions;
      }

      return unitOptions;
    };

    const getReferenceParameterOptions = (row: AlertRule): TOption[] => {
      if (row.data.sourceType === 'DRILLING_LOADS') {
        return drillingLoadsReferenceOptions;
      }

      if (row.data.sourceType !== 'GENERAL_PARAMETERS' || row.data.sourceOption == null) {
        return [];
      }

      const parameterType = getGeneralParameterCurve(row);
      const classOfUnitId = parameterType?.data['classOfUnitId'];

      const formatCurveName = (log: WellLog, curve: LogCurve) => {
        const name =
          isAltNames && curve.parameter.generalName ? curve.parameter.generalName : curve.parameter.displayMnemonic;
        const runNumber = log.runNumber && log.runNumber.length ? ` (${log.runNumber.join(', ')})` : '';
        const logName = (log.logName?.name || log.goStreamName) + runNumber;

        return [log.service?.name, logName, name].filter(Boolean).join(' / ');
      };

      return alertRulesService.wellboreLogs.flatMap((log) =>
        (classOfUnitId
          ? log.curves.filter((curve) => curve.parameter.classOfUnitId === classOfUnitId)
          : log.curves
        ).map((curve) => ({
          label:
            isAltNames && curve.parameter.generalName ? curve.parameter.generalName : curve.parameter.displayMnemonic,
          optionLabel: formatCurveName(log, curve),
          value: curve.goStreamId,
        })),
      );
    };

    const baseColumn: Omit<TTableColumn, 'field'> = {
      resizable: true,
      disableColumnMenu: editing,
      disableReorder: !editing,
      sortable: !editing,
      editable: editing,
      minWidth: 61,
      headerAlign: 'left',
      align: 'left',
      valueGetter(_, row, col) {
        if (col.field != null) {
          return store.getFieldValue(row.entityId as string, col.field as keyof TAlertRule);
        }

        return null;
      },
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_COLUMN_TITLE_KEY[params.field]}
          tableSubHeader={ALERT_RULE_GROUPED_COLUMN_FIELDS.has(params.field)}
        />
      ),

      renderCell(params) {
        return <AlertCell params={params} validationService={alertRulesService.validationService} />;
      },
    };

    const scaleDepthValue = (value: number) => {
      if (!alertRulesService.uiState.editing) {
        return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
      }
      return value;
    };

    return [
      {
        ...baseColumn,
        field: 'notificationsEnabled',
        type: 'boolean',
        sortable: false,
        disableColumnMenu: true,
        renderHeader: () => <NotificationsHeader service={alertRulesService} disabled={editing} />,
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        resizable: false,
        editable: false,
        valueFormatter: (value) => formatBoolean(value, i18n),
        headerClassName: clsx(styles.tableCellCenter, tableStyles.tableHeaderAction),
        cellClassName: tableStyles.tableCellAction,
        renderCell(params) {
          const value = alertRulesService.alertRulesMap.get(params.id as string)?.notificationsEnabled;
          if (value == null) {
            return null;
          }
          return (
            <TableCell style={{ justifyContent: 'center' }}>
              <TableCheckbox
                cellParams={params}
                checked={value}
                onChange={(value) => alertRulesService.setAlertRuleNotificationsOption(params.id as string, value)}
                disabled={editing}
              />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        field: 'journalEnabled',
        type: 'boolean',
        sortable: false,
        disableColumnMenu: true,
        renderHeader: () => <JournalHeaderCheckbox service={alertRulesService} disabled={!editing} />,
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        resizable: false,
        editable: false,
        valueFormatter: (value) => formatBoolean(value, i18n),
        headerClassName: clsx(styles.tableCellCenter, tableStyles.tableHeaderAction),
        cellClassName: tableStyles.tableCellAction,
        renderCell(params) {
          return (
            <TableCell style={{ justifyContent: 'center' }}>
              <TableCheckbox
                cellParams={params}
                checked={!!params.value}
                onChange={(value) => store.onAlertRuleItemValueChange(params.id as string, 'journalEnabled', value)}
                disabled={!editing}
              />
            </TableCell>
          );
        },
      },
      {
        field: 'context',
        type: 'actions',
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        sortable: false,
        filterable: false,
        editable: false,
        resizable: false,
        disableColumnMenu: true,
        disableReorder: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Tooltip title={t('alertsManagerWidget:alertRules.columnTitle.context')} delay={400}>
            <VerticalCustom style={{ fontSize: 16 }} />
          </Tooltip>
        ),
        headerClassName: tableStyles.tableHeaderAction,
        cellClassName: clsx(tableStyles.tableCellPurple, tableStyles.tableCellAction),
        renderCell(params) {
          if (editing) {
            return (
              <TableCell style={{ justifyContent: 'center' }}>
                <IconButton
                  className={tableStyles.tableCellActionButton}
                  icon={<ContextIcon entity={params.row} service={alertRulesService} />}
                  onClick={() => {
                    store.setSelectedRows([params.row.entityId]);
                    store.setIsContextPanelOpen(true);
                  }}
                />
              </TableCell>
            );
          }
          const hasContext = AlertRule.hasContext(params.row);
          if (hasContext) {
            return (
              <TableCell style={{ justifyContent: 'center' }}>
                <ContextIcon entity={params.row} service={alertRulesService} />
              </TableCell>
            );
          }
          return null;
        },
      },
      {
        ...baseColumn,
        field: 'riskLevel',
        type: 'singleSelect',
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        resizable: false,
        valueOptions: riskLevelOptions,
        renderCell(params) {
          const riskLevel = riskLevelOptions.find((option) => option.value === params.value);
          return (
            riskLevel && (
              <Tag
                status={riskLevel.status as TStatus}
                block
                className={styles.riskLevelTag}
                style={{ margin: '4px 8px' }}
              >
                {riskLevel?.label}
              </Tag>
            )
          );
        },
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={riskLevelOptions}
              onChange={(value) => store.onAlertRuleItemValueChange(params.id as string, 'riskLevel', value)}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'author',
        type: 'string',
        editable: false,
        valueGetter(_, row, col) {
          if (col.field != null) {
            return store.getFieldValue(row.entityId as string, col.field as keyof TAlertRule);
          }

          return null;
        },
        renderCell(params) {
          return (
            <UserInfo
              createdBy={params.row.createdBy}
              updatedBy={params.row.updatedBy}
              updatedAt={params.row.updatedAt}
              createdAt={params.row.createdAt}
              timeZonesStore={timeZonesStore}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'anomalyTypeId',
        type: 'singleSelect',
        width: 130,
        valueOptions: anomalyTypeOptions,
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={anomalyTypeOptions}
              showSearch
              optionFilterProp="label"
              onChange={(value) => store.onAlertRuleItemValueChange(params.id as string, 'anomalyTypeId', value)}
              allowClear
              onClear={() => store.onAlertRuleItemValueChange(params.id as string, 'anomalyTypeId', undefined)}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'anomalySubtypeId',
        type: 'singleSelect',
        width: 160,
        valueOptions: (params) => {
          const anomalyTypeId = params.row?.data?.anomalyTypeId;
          return anomalyTypeId ? getAnomalySubtypeOptions(anomalyTypeId) : [];
        },
        renderEditCell(params) {
          return (
            <TableSelect
              showSearch
              optionFilterProp="label"
              cellParams={params}
              value={params.value}
              autoFocus
              defaultOpen
              options={
                params.row && params.row.data.anomalyTypeId
                  ? getAnomalySubtypeOptions(params.row.data.anomalyTypeId)
                  : []
              }
              onChange={(value) => store.onAlertRuleItemValueChange(params.id as string, 'anomalySubtypeId', value)}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'sectionTypeIds',
        type: 'singleSelect',
        width: 160,
        sortable: false,
        filterable: false,
        valueOptions: sectionTypeOptions,
        valueFormatter: (value) => {
          const values = Array.isArray(value) ? value : [];

          return values
            .map((id) => sectionTypeOptions.find((option) => option.value === id)?.label)
            .filter(Boolean)
            .join(', ');
        },
      },
      {
        ...baseColumn,
        field: 'sourceType',
        type: 'singleSelect',
        width: 160,
        valueOptions: sourceTypeOptions,
        renderEditCell(params) {
          return (
            <TableSelect
              showSearch
              optionFilterProp="label"
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={sourceTypeOptions}
              onChange={(value) => store.onAlertRuleItemValueChange(params.id as string, 'sourceType', value)}
              allowClear
              onClear={() => store.onAlertRuleItemValueChange(params.id as string, 'sourceType', undefined)}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'sourceOption',
        type: 'singleSelect',
        valueOptions: (params) => {
          const sourceType = params.row?.data?.sourceType;
          return sourceType
            ? getSourceTypeOptions(sourceType)
            : uniqueOptions(sourceTypeOptions.flatMap((option) => getSourceTypeOptions(option.value as TSourceType)));
        },
        renderEditCell(params) {
          const sourceType = params.row.data.sourceType;
          return (
            <TableSelect
              autoFocus
              showSearch
              optionFilterProp="label"
              defaultOpen
              cellParams={params}
              value={params.value}
              options={getSourceTypeOptions(sourceType)}
              onChange={(value: BaseParameterTypes) => {
                store.onAlertRuleItemValueChange(params.id as string, 'sourceOption', value);
              }}
              allowClear
              onClear={() => store.onAlertRuleItemValueChange(params.id as string, 'sourceOption', undefined)}
            />
          );
        },
      },
      ...(alertRulesService.index === 'depth'
        ? []
        : ([
            {
              ...baseColumn,
              field: 'holeDepthFrom',
              type: 'number',
              width: 60,
              valueFormatter(_value) {
                if (_value == null) {
                  return '-';
                }

                const value = Number(_value);

                if (!Number.isNaN(value)) {
                  return scaleDepthValue(value);
                }

                return '-';
              },
              renderEditCell(params) {
                return (
                  <TableNumberInput
                    cellParams={params}
                    value={params.value}
                    allowClear
                    onChange={(value) => {
                      const parsedValue = (() => {
                        if (value == null || Number.isNaN(value)) {
                          return undefined;
                        }

                        return Math.round(value * 100) / 100;
                      })();

                      store.onAlertRuleItemValueChange(params.id as string, 'holeDepthFrom', parsedValue);
                    }}
                  />
                );
              },
            },
            {
              ...baseColumn,
              field: 'holeDepthTo',
              type: 'number',
              width: 60,
              valueFormatter(_value) {
                if (_value == null) {
                  return '-';
                }

                const value = Number(_value);

                if (!Number.isNaN(value)) {
                  return scaleDepthValue(value);
                }

                return '-';
              },
              renderEditCell(params) {
                return (
                  <TableNumberInput
                    cellParams={params}
                    value={params.value}
                    allowClear
                    onChange={(value) => {
                      const parsedValue = (() => {
                        if (value == null || Number.isNaN(value)) {
                          return undefined;
                        }

                        return Math.round(value * 100) / 100;
                      })();

                      store.onAlertRuleItemValueChange(params.id as string, 'holeDepthTo', parsedValue);
                    }}
                  />
                );
              },
            },
          ] satisfies GridColDef<AlertRule>[])),
      {
        ...baseColumn,
        field: 'depthFrom',
        type: 'number',
        width: 74,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return scaleDepthValue(value);
          }

          return '-';
        },
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              allowClear
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'depthFrom', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'depthTo',
        type: 'number',
        width: 74,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return scaleDepthValue(value);
          }

          return '-';
        },
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              allowClear
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'depthTo', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'unitId',
        type: 'singleSelect',
        valueOptions: (params) => (params.row ? unitOptionsFiltered(params.row) : []),
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={unitOptionsFiltered(params.row)}
              onChange={(value) => {
                store.onAlertRuleItemValueChange(params.id as string, 'unitId', value);
              }}
              showSearch
              optionFilterProp="label"
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'minValue',
        type: 'number',
        width: 60,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderEditCell(params) {
          const minGoStreamCurveId = store.getFieldValue(params.id as string, 'minGoStreamCurveId');
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              allowClear
              disabled={minGoStreamCurveId != null}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'minValue', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'minReference',
        type: 'singleSelect',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        valueOptions: (params) => (params.row ? getReferenceParameterOptions(params.row) : []),
        cellClassName: (params) =>
          params.row &&
          (params.row.data.sourceType === 'AUTOSUPERVISOR_KPI_STATES' ||
            params.row.data.sourceType === 'AUTOSUPERVISOR_OPERATIONS') &&
          editing
            ? styles.tableCellDisabled
            : styles.tableCellBlue,
        valueGetter(_, row, col) {
          if (col.field == null) {
            return null;
          }

          if (row.data.sourceType === 'DRILLING_LOADS') {
            return store.getFieldValue(row.entityId as string, 'minCommonParameterId');
          }
          if (row.data.sourceType === 'GENERAL_PARAMETERS') {
            return store.getFieldValue(row.entityId as string, 'minGoStreamCurveId');
          }

          return null;
        },
        renderEditCell(params) {
          const minValue = store.getFieldValue(params.id as string, 'minValue');
          const sourceType = store.getFieldValue(params.id as string, 'sourceType');
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              disabled={
                minValue != null ||
                sourceType === 'AUTOSUPERVISOR_KPI_STATES' ||
                sourceType === 'AUTOSUPERVISOR_OPERATIONS'
              }
              options={getReferenceParameterOptions(params.row)}
              optionFilterProp="optionLabel"
              optionRender={(item) => {
                return <>{item.data.optionLabel}</>;
              }}
              onChange={(value) => {
                if (params.row.data.sourceType === 'DRILLING_LOADS') {
                  store.onAlertRuleItemValueChange(params.id as string, 'minCommonParameterId', value);
                } else {
                  store.onAlertRuleItemValueChange(params.id as string, 'minGoStreamCurveId', value);
                }
              }}
              allowClear
              onClear={() => store.onAlertRuleItemValueChange(params.id as string, 'minGoStreamCurveId', undefined)}
              showSearch
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'minPercent',
        type: 'number',
        width: 60,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }
          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderEditCell(params) {
          const maxGoStreamCurveId = store.getFieldValue(params.id as string, 'maxGoStreamCurveId');
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              disabled={maxGoStreamCurveId != null}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'minPercent', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'maxValue',
        type: 'number',
        width: 60,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderEditCell(params) {
          const maxGoStreamCurveId = store.getFieldValue(params.id as string, 'maxGoStreamCurveId');
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              allowClear
              disabled={maxGoStreamCurveId != null}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'maxValue', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'maxReference',
        type: 'singleSelect',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        valueOptions: (params) => (params.row ? getReferenceParameterOptions(params.row) : []),
        cellClassName: (params) =>
          params.row &&
          (params.row.data.sourceType === 'AUTOSUPERVISOR_KPI_STATES' ||
            params.row.data.sourceType === 'AUTOSUPERVISOR_OPERATIONS') &&
          editing
            ? styles.tableCellDisabled
            : styles.tableCellBlue,
        valueGetter(_, row, col) {
          if (col.field == null) {
            return null;
          }

          if (row.data.sourceType === 'DRILLING_LOADS') {
            return store.getFieldValue(row.entityId as string, 'maxCommonParameterId');
          }
          if (row.data.sourceType === 'GENERAL_PARAMETERS') {
            return store.getFieldValue(row.entityId as string, 'maxGoStreamCurveId');
          }

          return null;
        },
        renderEditCell(params) {
          const maxValue = store.getFieldValue(params.id as string, 'maxValue');
          const sourceType = store.getFieldValue(params.id as string, 'sourceType');
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              disabled={
                maxValue != null ||
                sourceType === 'AUTOSUPERVISOR_KPI_STATES' ||
                sourceType === 'AUTOSUPERVISOR_OPERATIONS'
              }
              options={getReferenceParameterOptions(params.row)}
              optionFilterProp="optionLabel"
              optionRender={(item) => {
                return <>{item.data.optionLabel}</>;
              }}
              onChange={(value) => {
                if (params.row.data.sourceType === 'DRILLING_LOADS') {
                  store.onAlertRuleItemValueChange(params.id as string, 'maxCommonParameterId', value);
                } else {
                  store.onAlertRuleItemValueChange(params.id as string, 'maxGoStreamCurveId', value);
                }
              }}
              allowClear
              onClear={() => store.onAlertRuleItemValueChange(params.id as string, 'maxGoStreamCurveId', undefined)}
              showSearch
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'maxPercent',
        type: 'number',
        width: 60,
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }
          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'maxPercent', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'diffValue',
        type: 'number',
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'diffValue', parsedValue);
              }}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'diffInterval',
        type: 'number',
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          if (alertRulesService.index === 'depth') {
            return scaleDepthValue(_value);
          }

          return _value;
        },
        cellClassName: styles.tableCellBlue,
        renderHeader() {
          let unit = 'min';
          if (alertRulesService.index === 'depth') {
            unit = alertRulesService.uiState.editing ? defaultDepthUnit : scaleDepthUnit;
          }
          return (
            <TableHeader
              field={'diffInterval'}
              textTKey={FIELD_TO_COLUMN_TITLE_KEY['diffInterval']}
              unit={unit}
              tableSubHeader
            />
          );
        },
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value}
              onChange={(value) => {
                const parsedValue = (() => {
                  if (value == null || Number.isNaN(value)) {
                    return undefined;
                  }

                  return Math.round(value * 100) / 100;
                })();

                store.onAlertRuleItemValueChange(params.id as string, 'diffInterval', parsedValue);
              }}
            />
          );
        },
      },
    ];
  }, [
    editing,
    riskLevelOptions,
    anomalyTypeOptions,
    sectionTypeOptions,
    sourceTypeOptions,
    alertRulesService,
    store,
    unitOptions,
    isAltNames,
    defaultDepthUnit,
    scaleDepthUnit,
    t,
    i18n,
    timeZonesStore,
    getAnomalySubtypeOptions,
    getSourceTypeOptions,
    parameterTypeByMnemonic,
    drillingLoadsReferenceOptions,
  ]);

  return columns;
};
